import { Alert, Button, Group, Modal, NumberInput, Paper, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EntitySelect } from '../../components/EntityField';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import {
  currencySymbol,
  formatDate,
  formatMoney,
  type NumberInputValue,
  toMinorUnits,
  typedNumber,
} from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { useStockItems } from '../inventory/stock';

const ORDERS_PATH = '/admin/v1/suppliers/purchase-orders';
const REORDER_PATH = '/admin/v1/inventory/reorder-worklist';

interface Line {
  /** Lines are added and removed while the form is open, so each carries its own React key. */
  key: string;
  inventoryItemId: string;
  qty: NumberInputValue;
  unitPrice: NumberInputValue;
  /** The catalogue row this price came from, so the order records what it was priced against. */
  supplierCatalogItemId: string | null;
}

let keySeq = 0;
const blankLine = (): Line => ({
  key: `line-${++keySeq}`,
  inventoryItemId: '',
  qty: '',
  unitPrice: '',
  supplierCatalogItemId: null,
});

interface SupplierRow {
  id: string;
  name: string;
  invoiceCurrency?: string | null;
  minimumOrderMinor?: number | null;
  defaultLeadTimeDays?: number | null;
}

/**
 * When this vendor next comes, worked out on the server.
 *
 * The day in question is the island's, and this browser is on whatever clock and zone the laptop
 * happens to hold. A manager on a phone still set to Miami must not be told about a Tuesday that
 * is not Roatán's Tuesday, so the arithmetic is not repeated here.
 */
interface NextDelivery {
  today: string;
  deliveryDays: number[];
  deliveryDaysText: string | null;
  leadTimeDays: number | null;
  nextRunDate: string | null;
  makeableRunDate: string | null;
  missesTheNextRun: boolean;
  daysAway: number | null;
}

interface CatalogItem {
  id: string;
  inventoryItemId: string;
  /** How many of the item's own counted units come in one pack: a case of 12 bottles is 12. */
  packQty: number;
  /** What the PACK costs them, not one unit of it — see `perUnitMinor`. */
  priceMinor: number;
  currency: string;
  supersededAt?: string | null;
}

/**
 * Their list price brought down to one counted unit.
 *
 * A price list is written the way they sell: a case of twelve for two thousand four hundred. An
 * order line is written the way the bar counts: bottles. Copying the case price onto a per-bottle
 * line would put an order in at twelve times what it is worth, and the supplier would be the one
 * to notice.
 */
function perUnitMinor(row: CatalogItem): number {
  return Math.round(row.priceMinor / Math.max(1, row.packQty || 1));
}

interface ShortItem {
  item: { id: string; nameEn: string; parLevel: number | null; reorderPoint: number | null };
  totalOnHand: number;
}

/** Above this many things they sell, finding one by eye stops working and a filter box appears. */
const NEEDS_FILTERING = 12;

/**
 * Writing down an order that has gone out to a supplier.
 *
 * Opened from the vendor's own page the supplier is not a question — the screen underneath was
 * already about them — and THEIR PRICE LIST IS THE ORDER FORM. What you buy from them is already
 * on file with pack sizes and prices; putting a quantity beside each is the whole job. Picking
 * every item back out of a dropdown of every stock item the business buys was re-answering a
 * question the vendor's page had already answered, one line at a time.
 *
 * Anything they sell with no price on file still goes on by hand, underneath, as it always did.
 *
 * The running-low list fills the order in one press: it already knows what is short and by how
 * much, and re-deriving that by hand is the step where a case gets forgotten.
 */
export function PlaceOrderForm({
  locationId,
  supplierId: fixedSupplierId,
  onClose,
  onSaved,
}: {
  locationId?: string;
  /** Set when the order is placed from the vendor's own page; then it is never asked for. */
  supplierId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const stock = useStockItems();
  const items = useEntityIndex('inventoryItem');

  const [supplierId, setSupplierId] = useState(fixedSupplierId ?? '');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const suppliersQuery = useQuery({
    queryKey: listKey('/admin/v1/suppliers/suppliers'),
    queryFn: () => rawApi<SupplierRow[]>('GET', '/admin/v1/suppliers/suppliers'),
    staleTime: 60_000,
  });
  const supplier = (suppliersQuery.data ?? []).find((s) => s.id === supplierId);
  const currency = supplier?.invoiceCurrency ?? 'HNL';

  const catalogQuery = useQuery({
    queryKey: listKey(`/admin/v1/suppliers/suppliers/${supplierId}/catalog`),
    queryFn: () => rawApi<CatalogItem[]>('GET', `/admin/v1/suppliers/suppliers/${supplierId}/catalog`),
    enabled: Boolean(supplierId),
    staleTime: 60_000,
  });
  // Their current price for each thing, ignoring the rows a newer price has replaced.
  const priceOf = new Map<string, CatalogItem>();
  for (const row of catalogQuery.data ?? []) {
    if (!row.supersededAt) priceOf.set(row.inventoryItemId, row);
  }
  const catalog = [...priceOf.values()].sort((a, b) =>
    items.label(a.inventoryItemId).localeCompare(items.label(b.inventoryItemId)),
  );
  const needle = filter.trim().toLowerCase();
  const shown = needle
    ? catalog.filter((row) => items.label(row.inventoryItemId).toLowerCase().includes(needle))
    : catalog;

  // A vendor with no prices on file has nothing to put a quantity against, so the hand-typed line
  // is the form and it opens ready to type into rather than behind a button.
  const noCatalogue = Boolean(supplierId) && catalogQuery.isSuccess && catalog.length === 0;
  useEffect(() => {
    if (noCatalogue) setLines((current) => (current.length === 0 ? [blankLine()] : current));
  }, [noCatalogue]);

  // Only once a vendor is chosen: on the general form there is nobody to ask about yet.
  const nextRun = useQuery({
    queryKey: listKey(`/admin/v1/suppliers/suppliers/${supplierId}/next-delivery`),
    queryFn: () => rawApi<NextDelivery>('GET', `/admin/v1/suppliers/suppliers/${supplierId}/next-delivery`),
    enabled: Boolean(supplierId),
    staleTime: 60_000,
  });

  const shortQuery = useQuery({
    queryKey: listKey(REORDER_PATH),
    queryFn: () => rawApi<ShortItem[]>('GET', REORDER_PATH),
    staleTime: 30_000,
  });
  const shortOf = new Map((shortQuery.data ?? []).map((s) => [s.item.id, s]));
  // Only what this supplier actually sells, and only what has not been given a quantity yet.
  const short = (shortQuery.data ?? []).filter(
    (s) => priceOf.has(s.item.id) && !lines.some((l) => l.inventoryItemId === s.item.id && l.qty !== ''),
  );

  const setLine = (index: number, patch: Partial<Line>) =>
    setLines(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  // Their price only when it is in the money this order is priced in. A dollar list price dropped
  // into a lempira order is a twenty-six-fold error that looks like a filled-in field.
  const listPriceFor = (inventoryItemId: string) => {
    const row = priceOf.get(inventoryItemId);
    return row && row.currency === currency ? row : undefined;
  };

  /** Choosing an item on a hand-typed line brings their price with it, if they have one on file. */
  const pickItem = (index: number, inventoryItemId: string) => {
    const priced = listPriceFor(inventoryItemId);
    setLine(index, {
      inventoryItemId,
      supplierCatalogItemId: priceOf.get(inventoryItemId)?.id ?? null,
      ...(lines[index]?.unitPrice === '' && priced ? { unitPrice: perUnitMinor(priced) / 100 } : {}),
    });
  };

  const lineFor = (inventoryItemId: string) => lines.find((l) => l.inventoryItemId === inventoryItemId);

  /**
   * A quantity typed against something on their price list. The line comes into being when a
   * number is typed into it, so the order is exactly the rows somebody put a figure against.
   */
  const orderQty = (row: CatalogItem, qty: NumberInputValue) => {
    const existing = lines.findIndex((l) => l.inventoryItemId === row.inventoryItemId);
    if (existing >= 0) return setLine(existing, { qty });
    if (qty === '') return;
    const priced = listPriceFor(row.inventoryItemId);
    setLines([
      ...lines,
      {
        ...blankLine(),
        inventoryItemId: row.inventoryItemId,
        qty,
        unitPrice: priced ? perUnitMinor(priced) / 100 : '',
        supplierCatalogItemId: row.id,
      },
    ]);
  };

  const fillFromShort = () => {
    const patched = [...lines];
    const added: Line[] = [];
    for (const s of short) {
      const priced = listPriceFor(s.item.id);
      const target = s.item.parLevel ?? s.item.reorderPoint ?? 0;
      // Enough to get back to what he likes to hold, which is the number the running-low list
      // already works out and the one he would otherwise redo in his head, item by item.
      const qty = Math.max(0, Math.round((target - s.totalOnHand) * 1000) / 1000) || '';
      const at = patched.findIndex((l) => l.inventoryItemId === s.item.id);
      if (at >= 0) patched[at] = { ...patched[at]!, qty };
      else {
        added.push({
          ...blankLine(),
          inventoryItemId: s.item.id,
          qty,
          unitPrice: priced ? perUnitMinor(priced) / 100 : '',
          supplierCatalogItemId: priceOf.get(s.item.id)?.id ?? null,
        });
      }
    }
    setLines([...patched, ...added]);
  };

  /** Lines they have no price on file for: typed by hand, with their own item picker. */
  const extras = lines.filter((l) => !l.inventoryItemId || !priceOf.has(l.inventoryItemId));
  const filled = lines.filter((l) => l.inventoryItemId && l.qty !== '');
  const totalMinor = filled.reduce(
    (sum, l) => sum + Math.round((toMinorUnits(l.unitPrice) ?? 0) * (typedNumber(l.qty) ?? 0)),
    0,
  );
  const belowMinimum =
    typeof supplier?.minimumOrderMinor === 'number' &&
    supplier.minimumOrderMinor > 0 &&
    totalMinor > 0 &&
    totalMinor < supplier.minimumOrderMinor;

  const place = useMutation({
    mutationFn: async () => {
      const created = await rawApi<{ po: { id: string } }>('POST', ORDERS_PATH, {
        locationId,
        supplierId,
        note: note.trim() || null,
        lines: filled.map((l) => ({
          inventoryItemId: l.inventoryItemId,
          supplierCatalogItemId: l.supplierCatalogItemId,
          qty: Number(l.qty),
          unit: stock.unitOf(l.inventoryItemId),
          unitPriceMinor: toMinorUnits(l.unitPrice) ?? 0,
          currency,
        })),
      });
      // Recorded as already sent, because that is what happened: the order went out on WhatsApp
      // and this screen is writing it down. An order left as a draft counts as nothing on order
      // and cannot be picked when the goods turn up.
      await rawApi('POST', `${ORDERS_PATH}/${created.po.id}/status`, { status: 'sent' });
    },
    onSuccess: () => {
      // The bare path, which is the prefix of this vendor's own filtered key too: the Orders tab
      // and their page cache separately, and a saved order missing from the screen that saved it
      // reads as a failure.
      void queryClient.invalidateQueries({ queryKey: listKey(ORDERS_PATH) });
      onSaved();
      onClose();
      notifyDone(
        'Order recorded',
        `${filled.length} ${filled.length === 1 ? 'item' : 'items'} on order from ${supplier?.name ?? 'them'}. It will be offered when you record the delivery.`,
      );
    },
    onError: (err: Error) => notifyError(err, 'record this order'),
  });

  const submit = () => {
    if (!supplierId) return setError('Say who the order went to');
    if (filled.length === 0) return setError('Put a quantity against at least one thing');
    const noQty = filled.find((l) => (typedNumber(l.qty) ?? 0) <= 0);
    if (noQty) return setError(`Say how much ${items.label(noQty.inventoryItemId)} you asked for`);
    const noUnit = filled.find((l) => !stock.unitOf(l.inventoryItemId));
    if (noUnit) {
      return setError(
        `${items.label(noUnit.inventoryItemId)} has no unit set — fill in "Counted in" under Stock items first`,
      );
    }
    const twice = filled.find(
      (l, i) => filled.findIndex((o) => o.inventoryItemId === l.inventoryItemId) !== i,
    );
    if (twice) {
      return setError(
        `${items.label(twice.inventoryItemId)} is on the order twice — put the whole amount on one line`,
      );
    }
    setError(null);
    place.mutate();
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={fixedSupplierId && supplier ? `Order from ${supplier.name}` : 'Record an order'}
      size="xl"
      // Half-completed forms are lost to a stray click otherwise.
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Write down what you have asked {fixedSupplierId && supplier ? supplier.name : 'a supplier'} for. It
          shows against them as on order, and when the goods arrive the delivery can be recorded against it.
        </Text>

        <Group grow align="flex-start">
          {/* Not asked when the screen underneath is already about one vendor: that the answer is
              not in question is the whole point of ordering from their page. */}
          {!fixedSupplierId && (
            <EntitySelect
              // Not every vendor — only the ones you buy goods from. The ferry and the freight
              // forwarders bill for crossings, not for limes; see `entities.ts`.
              entity="goodsSupplier"
              label="Who the order went to"
              required
              value={supplierId || null}
              onChange={(v) => {
                setSupplierId(v ?? '');
                setLines([]);
              }}
              disabled={place.isPending}
            />
          )}
          <TextInput
            label="What it is for"
            description="How you would recognise this order later — the weekend restock, the party on the 14th"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            disabled={place.isPending}
          />
        </Group>

        {supplier && (
          <Text size="xs" c="dimmed">
            Priced in {currency === 'USD' ? 'dollars' : 'lempiras'}, the way they bill you
            {supplier.defaultLeadTimeDays
              ? ` · usually about ${supplier.defaultLeadTimeDays} ${supplier.defaultLeadTimeDays === 1 ? 'day' : 'days'} to arrive`
              : ''}
          </Text>
        )}

        {supplierId && catalog.length > 0 && (
          <div>
            <Group justify="space-between" align="flex-end" mb={6}>
              <div>
                <Text size="sm" fw={500}>
                  What you buy from them
                </Text>
                <Text size="xs" c="dimmed">
                  Put a quantity beside anything you are ordering. The prices are theirs, per the unit you
                  count in — change one if they quoted you differently.
                </Text>
              </div>
              {/* The one press that turns "these are running low" into an order. Only offered when
                  this supplier sells something on that list; otherwise it is a button that does
                  nothing and leaves the reader wondering what they got wrong. */}
              {short.length > 0 && (
                <Button size="compact-sm" variant="light" onClick={fillFromShort} disabled={place.isPending}>
                  Add the {short.length} running low
                </Button>
              )}
            </Group>

            {catalog.length > NEEDS_FILTERING && (
              <TextInput
                placeholder="Find something they sell"
                mb="xs"
                value={filter}
                onChange={(e) => setFilter(e.currentTarget.value)}
                disabled={place.isPending}
              />
            )}

            <Stack gap={4}>
              {shown.map((row) => {
                const line = lineFor(row.inventoryItemId);
                const unit = stock.unitOf(row.inventoryItemId);
                const ordering = line !== undefined && line.qty !== '';
                const lineTotal = Math.round(
                  (toMinorUnits(line?.unitPrice) ?? 0) * (typedNumber(line?.qty) ?? 0),
                );
                return (
                  <Paper key={row.id} withBorder={ordering} p="xs" radius="sm">
                    <Group gap="xs" align="flex-end" wrap="nowrap">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={ordering ? 600 : 400}>
                          {items.label(row.inventoryItemId)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatMoney(perUnitMinor(row), row.currency)}
                          {unit ? ` a ${unit}` : ' each'}
                          {row.packQty > 1
                            ? ` · ${formatMoney(row.priceMinor, row.currency)} for ${row.packQty}`
                            : ''}
                          {/* Their quote is in the other money: filling it in would be a
                              twenty-six-fold error that looks like a completed field. */}
                          {row.currency !== currency
                            ? ' · quoted in the other money, so type what they charge'
                            : ''}
                          {shortOf.has(row.inventoryItemId) ? ' · running low' : ''}
                        </Text>
                      </div>
                      <NumberInput
                        aria-label={`How much ${items.label(row.inventoryItemId)}`}
                        placeholder="0"
                        min={0}
                        style={{ width: 130 }}
                        suffix={unit ? ` ${unit}` : undefined}
                        value={line?.qty ?? ''}
                        onChange={(v) => orderQty(row, v)}
                        disabled={place.isPending}
                      />
                      {/* The price box appears only on the rows being ordered: forty editable
                          prices down a page is a form, and this is meant to be a list. */}
                      {ordering ? (
                        <NumberInput
                          aria-label={`Price per ${unit || 'unit'}`}
                          min={0}
                          style={{ width: 150 }}
                          prefix={`${currencySymbol(currency)} `}
                          decimalScale={2}
                          thousandSeparator=","
                          hideControls
                          value={line?.unitPrice ?? ''}
                          onChange={(v) => {
                            const at = lines.findIndex((l) => l.inventoryItemId === row.inventoryItemId);
                            if (at >= 0) setLine(at, { unitPrice: v });
                          }}
                          disabled={place.isPending}
                        />
                      ) : (
                        <div style={{ width: 150 }} />
                      )}
                      <Text size="sm" fw={600} ta="right" pb={6} style={{ width: 110 }}>
                        {lineTotal > 0 ? formatMoney(lineTotal, currency) : ''}
                      </Text>
                    </Group>
                  </Paper>
                );
              })}
              {shown.length === 0 && (
                <Text size="sm" c="dimmed" fs="italic">
                  Nothing they sell matches that.
                </Text>
              )}
            </Stack>
          </div>
        )}

        {supplierId && (
          <div>
            <Text size="sm" fw={500}>
              {catalog.length > 0 ? 'Anything else you are asking them for' : 'What you ordered'}
            </Text>
            <Text size="xs" c="dimmed" mb={6}>
              {catalog.length > 0
                ? 'Things they have no price on file for. Record their price on their page and it fills itself in next time.'
                : 'No prices on file for them yet. Record what they charge on their page and an order to them fills its own prices in.'}
            </Text>
            <Stack gap="xs">
              {extras.map((line) => {
                const index = lines.indexOf(line);
                const unit = stock.unitOf(line.inventoryItemId);
                const lineTotal = Math.round(
                  (toMinorUnits(line.unitPrice) ?? 0) * (typedNumber(line.qty) ?? 0),
                );
                return (
                  <Paper key={line.key} withBorder p="xs" radius="sm">
                    <Group gap="xs" align="flex-end" wrap="wrap">
                      <div style={{ minWidth: 240, flex: 1 }}>
                        <EntitySelect
                          entity="inventoryItem"
                          label="Item"
                          value={line.inventoryItemId || null}
                          onChange={(v) => pickItem(index, v ?? '')}
                          disabled={place.isPending}
                        />
                      </div>
                      <NumberInput
                        label="How much"
                        min={0}
                        style={{ width: 150 }}
                        suffix={unit ? ` ${unit}` : undefined}
                        value={line.qty}
                        onChange={(v) => setLine(index, { qty: v })}
                        disabled={place.isPending}
                      />
                      <NumberInput
                        label={unit ? `Price per ${unit}` : 'Price each'}
                        min={0}
                        style={{ width: 160 }}
                        prefix={`${currencySymbol(currency)} `}
                        decimalScale={2}
                        thousandSeparator=","
                        hideControls
                        value={line.unitPrice}
                        onChange={(v) => setLine(index, { unitPrice: v })}
                        disabled={place.isPending}
                      />
                      <Text size="sm" fw={600} pb={6} style={{ width: 110, textAlign: 'right' }}>
                        {lineTotal > 0 ? formatMoney(lineTotal, currency) : ''}
                      </Text>
                      <Button
                        size="compact-sm"
                        variant="subtle"
                        color="red"
                        onClick={() => setLines(lines.filter((l) => l.key !== line.key))}
                        disabled={place.isPending}
                      >
                        Remove
                      </Button>
                    </Group>
                  </Paper>
                );
              })}
              <Group justify="space-between">
                <Button
                  size="compact-sm"
                  variant="light"
                  onClick={() => setLines([...lines, blankLine()])}
                  disabled={place.isPending}
                >
                  {catalog.length > 0 ? 'Add something else' : 'Add an item'}
                </Button>
                <Text size="sm">
                  The order comes to <strong>{formatMoney(totalMinor, currency)}</strong>
                </Text>
              </Group>
            </Stack>
          </div>
        )}

        {/* WHEN THIS ORDER WILL ACTUALLY LAND, said while there is still something to do about it.
            On an island the boat is the supply chain: an order written on Thursday afternoon that
            cannot make Friday's run is four days of no limes, and the only moment that fact is
            worth anything is now — ring them, or order enough to last the gap. */}
        {supplierId && nextRun.data && (
          <WhenItWillLand when={nextRun.data} supplierName={supplier?.name ?? 'them'} />
        )}

        {/* Their minimum is recorded on the supplier for exactly this moment, and an order under it
            comes back as a refusal hours later, by WhatsApp, when the goods were needed today. */}
        {belowMinimum && supplier?.minimumOrderMinor && (
          <Alert color="yellow" variant="light" title="Under their minimum order">
            {supplier.name} will not deliver less than {formatMoney(supplier.minimumOrderMinor, currency)}.
            Add {formatMoney(supplier.minimumOrderMinor - totalMinor, currency)} more, or send it anyway if
            they have agreed to it.
          </Alert>
        )}

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={place.isPending}>
            Cancel
          </Button>
          <Button loading={place.isPending} onClick={submit}>
            Record the order
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * The sentence about their next run.
 *
 * Three states and each one earns its space. Their days are not on file: an invitation to record
 * them, once, on their page — because the fact is worth more than this one order. The order makes
 * the next run: a quiet confirmation. The order misses it: the warning, in the colour that means
 * "read this", naming both dates so the reader can decide rather than being told.
 */
function WhenItWillLand({ when, supplierName }: { when: NextDelivery; supplierName: string }) {
  if (when.deliveryDays.length === 0) {
    return (
      <Text size="xs" c="dimmed">
        Nobody has written down which days {supplierName} come. Record them on their page and an order will
        say which of their runs it can still make.
      </Text>
    );
  }

  const arrives = when.makeableRunDate ? formatDate(when.makeableRunDate) : null;

  if (when.missesTheNextRun && when.nextRunDate) {
    return (
      <Alert color="orange" variant="light" title="This will not be on their next run">
        {supplierName} come {when.deliveryDaysText}, and the next one is {formatDate(when.nextRunDate)}.
        {when.leadTimeDays !== null
          ? ` They usually take ${when.leadTimeDays} ${when.leadTimeDays === 1 ? 'day' : 'days'}, so an order sent now arrives ${arrives}`
          : ` An order sent now arrives ${arrives}`}
        {typeof when.daysAway === 'number' ? ` — ${when.daysAway} days away` : ''}. Ring them if it has to be
        sooner, or put enough on to last until then.
      </Alert>
    );
  }

  return (
    <Text size="xs" c="dimmed">
      {supplierName} come {when.deliveryDaysText} — sent now, this should arrive {arrives}.
    </Text>
  );
}
